import { useState, useEffect } from 'react';
import { Calendar, CheckSquare, Clock, Filter, Plus, Search, Trash2, X, Check, AlertCircle, ChevronLeft, ChevronRight, Bell, UserCheck } from 'lucide-react';
import { Dialog } from '@headlessui/react';
import CustomListbox from '../components/CustomListbox';
import { Todo, createDocument, getDocuments, updateDocument, deleteDocument } from '../lib/supabaseHelpers';
import { Influencer, normalizeInfluencer } from '../lib/supabase';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContextSupabase';

const PRIORITIES = [
  { value: 'low', label: 'Basse', color: 'bg-blue-500/20 text-blue-300 border-blue-500/20' },
  { value: 'medium', label: 'Moyenne', color: 'bg-yellow-500/20 text-yellow-300 border-yellow-500/20' },
  { value: 'high', label: 'Haute', color: 'bg-red-500/20 text-red-300 border-red-500/20' },
] as const;

const STATUSES = [
  { value: 'pending', label: 'En attente', color: 'bg-gray-500/20 text-gray-300 border-gray-500/20' },
  { value: 'in_progress', label: 'En cours', color: 'bg-yellow-500/20 text-yellow-300 border-yellow-500/20' },
  { value: 'completed', label: 'Terminée', color: 'bg-green-500/20 text-green-300 border-green-500/20' },
] as const;

const WEEKDAYS = ['Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi'];

function TodoPage() {
  const { currentUser, userProfile } = useAuth();
  const [todos, setTodos] = useState<Todo[]>([]);
  const [influencers, setInfluencers] = useState<Influencer[]>([]);
  const [userNames, setUserNames] = useState<Record<string, string>>({});
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedTodo, setSelectedTodo] = useState<Todo | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [priorityFilter, setPriorityFilter] = useState<string>('all');
  const [currentWeek, setCurrentWeek] = useState(new Date());
  const [agencyUsers, setAgencyUsers] = useState<{ id: string; firstName: string; lastName: string }[]>([]);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [formData, setFormData] = useState<Partial<Todo>>({
    title: '',
    description: '',
    status: 'pending',
    priority: 'medium',
    dueDate: undefined,
    assignedTo: undefined,
  });
  const [customTitle, setCustomTitle] = useState('');
  const [isInfluencerTask, setIsInfluencerTask] = useState(false);
  const [influencerPrefix, setInfluencerPrefix] = useState('');
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setIsLoading(true);
      
      // Fetch influencers from Supabase - limit to 100 with only necessary fields
      const { data: influencersData, error: influencersError } = await supabase
        .from('influencers')
        .select('id, display_name, full_name, username, profile_image_url, followers_count, category')
        .eq('archived', false)
        .order('followers_count', { ascending: false })
        .limit(100);

      if (influencersError) {
        console.error('Error loading influencers:', influencersError);
        setInfluencers([]);
      } else {
        setInfluencers((influencersData || []).map(normalizeInfluencer));
      }
      
      if (!currentUser) return;

      // Fetch agency users from Supabase
      const { data: usersData } = await supabase
        .from('users')
        .select('id, first_name, last_name, email')
        .eq('agency_id', userProfile?.agency_id);

      if (usersData) {
        const users = usersData.map(u => ({
          id: u.id,
          firstName: u.first_name,
          lastName: u.last_name
        }));
        setAgencyUsers(users);

        // Populate userNames map
        const names: Record<string, string> = {};
        usersData.forEach(u => {
          const displayName = u.first_name && u.last_name
            ? `${u.first_name} ${u.last_name}`
            : u.email?.split('@')[0] || 'Utilisateur';

          names[u.id] = displayName;
        });
        setUserNames(names);
      }

      // Fetch todos from Supabase
      const todosData = await getDocuments<Todo>('todos', userProfile?.agency_id || '');

      // Filter todos for current user
      const filteredTodos = todosData.filter(todo =>
        todo.title?.startsWith('Tâche pour ') ||
        todo.user_id === currentUser?.id ||
        todo.assigned_to === currentUser?.id
      );

      setTodos(filteredTodos);
    } catch (error) {
      console.error('Error loading data:', error);
      setError('Une erreur est survenue lors du chargement des données');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccessMessage(null);
    
    const isInfluencerTask = formData.title?.startsWith('Tâche pour ');
    
    if (!currentUser) {
      setError('Utilisateur non connecté');
      return;
    }

    if (!formData.dueDate && !selectedDate) {
      setError('La date est requise');
      return;
    }

    if (!currentUser || !userProfile?.agency_id) {
      setError('Utilisateur non connecté');
      return;
    }

    const dueDate = formData.dueDate ? new Date(formData.dueDate) : new Date(selectedDate!);
    dueDate.setHours(12, 0, 0, 0);

    // Pour les tâches d'influenceurs, pas besoin d'assignation spécifique
    const assignedTo = isInfluencerTask ? null : (
      formData.assignedTo && formData.assignedTo.trim() !== ''
        ? formData.assignedTo
        : currentUser?.id || ''
    );

    try {
      const todoData = {
        title: formData.title,
        description: formData.description || '',
        status: formData.status,
        priority: formData.priority,
        dueDate: dueDate.toISOString(),
        assignedTo,
        updatedAt: new Date(),
      };

      if (selectedTodo?.id) {
        await updateDocument('todos', selectedTodo.id, todoData);
      } else {
        const isAssigningToOther = formData.assignedTo && formData.assignedTo !== currentUser?.id;
        await createDocument('todos', {
          ...todoData,
          agency_id: userProfile.agency_id,
          user_id: currentUser?.id || '',
          status: 'pending'
        } as Todo);
        
        if (isAssigningToOther) {
          const assignedUser = agencyUsers.find(user => user.id === formData.assignedTo);
          if (assignedUser) {
            setSuccessMessage(`Tâche assignée à ${assignedUser.firstName} ${assignedUser.lastName}`);
          }
        }
      }
      await loadData();
      handleCloseModal();
    } catch (error) {
      console.error('Error saving todo:', error);
      setError('Une erreur est survenue lors de la sauvegarde');
    }
  };

  const handleStatusChange = async (todo: Todo, newStatus: Todo['status']) => {
    try {
      await updateDocument('todos', todo.id!, {
        status: newStatus,
        completed_at: newStatus === 'completed' ? new Date().toISOString() : null
      });
      await loadData();
    } catch (error) {
      console.error('Error updating todo status:', error);
    }
  };

  const handleDelete = async (todoId: string) => {
    try {
      // Update local state by removing the deleted todo
      setTodos(prevTodos => prevTodos.filter(todo => todo.id !== todoId));
      
      // Delete the todo from Supabase
      try {
        await deleteDocument('todos', todoId);
        console.log(`Tâche ${todoId} supprimée avec succès`);
      } catch (deleteError) {
        console.error('Error in deleteDocument operation:', deleteError);
        loadData();
        setError('Erreur lors de la suppression de la tâche');
      }
    } catch (error) {
      console.error('Error deleting todo:', error);
      setError('Erreur lors de la suppression de la tâche');
    }
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedTodo(null);
    setSelectedDate(null);
    setFormData({
      title: '',
      description: '',
      status: 'pending',
      priority: 'medium',
      assignedTo: undefined,
      dueDate: undefined,
    });
    setCustomTitle('');
    setIsInfluencerTask(false);
    setInfluencerPrefix('');
  };

  const handleAddTaskForDate = (date: Date) => {
    setSelectedDate(date);
    setFormData(prev => ({
      ...prev,
      dueDate: date.toISOString()
    }));
    setIsModalOpen(true);
  };

  const formatDate = (date: Date | undefined) => {
    if (!date) return '';
    return new Date(date).toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });
  };

  const getWeekDates = (date: Date) => {
    const monday = new Date(date);
    monday.setDate(monday.getDate() - monday.getDay() + 1);
    
    return WEEKDAYS.map((_, index) => {
      const day = new Date(monday);
      day.setDate(monday.getDate() + index);
      return day;
    });
  };

  const isValidDate = (date: any): date is Date => {
    return date instanceof Date && !isNaN(date.getTime());
  };

  const isSameDay = (date1: Date | string | undefined, date2: Date | string | undefined) => {
    if (!date1 || !date2) return false;
    
    try {
      const d1 = new Date(date1);
      const d2 = new Date(date2);
      
      if (!isValidDate(d1) || !isValidDate(d2)) return false;
      
      return d1.getFullYear() === d2.getFullYear() &&
             d1.getMonth() === d2.getMonth() &&
             d1.getDate() === d2.getDate();
    } catch (error) {
      console.error('Error comparing dates:', error);
      return false;
    }
  };

  const getTodosForDate = (date: Date) => {
    return todos.filter(todo => {
      // Ne pas afficher les tâches d'influenceurs dans le calendrier
      if (!todo.due_date || todo.title?.startsWith('Tâche pour ')) return false;
      try {
        return isSameDay(new Date(todo.due_date!), date);
      } catch (error) {
        console.error('Invalid todo date:', todo.due_date);
        return false;
      }
    });
  };

  const previousWeek = () => {
    const newDate = new Date(currentWeek);
    newDate.setDate(newDate.getDate() - 7);
    setCurrentWeek(newDate);
  };

  const nextWeek = () => {
    const newDate = new Date(currentWeek);
    newDate.setDate(newDate.getDate() + 7);
    setCurrentWeek(newDate);
  };

  const filteredTodos = todos.filter(todo => {
    const matchesSearch = 
      todo.title.toLowerCase().includes(search.toLowerCase()) ||
      (todo.description || '').toLowerCase().includes(search.toLowerCase());
    
    const matchesStatus = statusFilter === 'all' || todo.status === statusFilter;
    const matchesPriority = priorityFilter === 'all' || todo.priority === priorityFilter;

    return matchesSearch && matchesStatus && matchesPriority;
  });

  const weekDates = getWeekDates(currentWeek);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-xl bg-emerald-500/20 flex items-center justify-center">
            <CheckSquare className="w-6 h-6 text-emerald-400" />
          </div>
          <div>
            <h1 className="text-3xl font-bold text-white">Tâches</h1>
            <p className="text-white/40">Gérez vos tâches et suivez leur avancement</p>
          </div>
        </div>
      </div>

      <div className="rounded-xl border border-white/[0.08] bg-white/[0.04] p-6">
        {successMessage && (
          <div className="mb-6 bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 px-4 py-3 rounded-lg flex items-center gap-2">
            <Bell className="w-5 h-5 flex-shrink-0" />
            <p>{successMessage}</p>
          </div>
        )}

        {isLoading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white/40"></div>
          </div>
        ) : (
          <>
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-semibold text-white">
                Semaine du {formatDate(weekDates[0])} au {formatDate(weekDates[4])}
              </h2>
              <div className="flex items-center gap-2">
                <button
                  onClick={previousWeek}
                  className="p-2 text-white/40 hover:text-white rounded-lg hover:bg-white/[0.08] transition-colors"
                >
                  <ChevronLeft className="w-5 h-5" />
                </button>
                <button
                  onClick={nextWeek}
                  className="p-2 text-white/40 hover:text-white rounded-lg hover:bg-white/[0.08] transition-colors"
                >
                  <ChevronRight className="w-5 h-5" />
                </button>
              </div>
            </div>

            <div className="grid grid-cols-5 gap-4">
              {weekDates.map((date, index) => (
                <div key={index} className="rounded-xl border border-white/[0.06] bg-white/[0.03]">
                  <div className="p-3 border-b border-white/[0.06]">
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="text-white font-medium">{WEEKDAYS[index]}</h3>
                        <p className="text-xs text-white/40">{formatDate(date)}</p>
                      </div>
                      <button
                        onClick={() => handleAddTaskForDate(date)}
                        className="p-1 rounded-lg bg-white/[0.08] hover:bg-white/[0.14] text-white/60 hover:text-white transition-colors"
                        title="Ajouter une tâche"
                      >
                        <Plus className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                  <div className="p-3 space-y-2 min-h-[200px]">
                    {getTodosForDate(date).map((todo) => (
                      <div
                        key={todo.id}
                        className="rounded-lg p-2 cursor-pointer bg-white/[0.04] hover:bg-white/[0.08] transition-colors border border-white/[0.06]"
                        onClick={() => {
                          setSelectedTodo(todo);
                          setFormData({
                            title: todo.title,
                            description: todo.description,
                            status: todo.status,
                            priority: todo.priority,
                            dueDate: todo.due_date,
                          });
                          setIsModalOpen(true);
                        }}
                      >
                        <div className="flex items-center justify-between mb-1">
                          <div className="flex items-center gap-2 flex-1 min-w-0">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleStatusChange(todo, todo.status === 'completed' ? 'pending' : 'completed');
                              }}
                              className={`p-1 rounded-lg transition-colors ${
                                todo.status === 'completed'
                                  ? 'bg-emerald-500/20 text-emerald-400'
                                  : 'bg-white/10 text-white/40'
                              }`}
                            >
                              <Check className="w-4 h-4" />
                            </button>
                            <span className="text-white/80 text-sm truncate">{todo.title}</span>
                          </div>
                          <button
                            onClick={(e: React.MouseEvent) => {
                              e.stopPropagation();
                              handleDelete(todo.id!);
                            }}
                            className="text-red-400/60 hover:text-red-400 flex-shrink-0 ml-2"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                            todo.priority === 'high' ? 'bg-red-500/20 text-red-400' :
                            todo.priority === 'medium' ? 'bg-amber-500/20 text-amber-400' :
                            'bg-blue-500/20 text-blue-400'
                          }`}>
                            {PRIORITIES.find(p => p.value === todo.priority)?.label}
                          </span>
                        </div>
                        <div className="flex items-center gap-2 mt-1">
                          <span className="text-xs text-white/30">
                            Par {userNames[todo.user_id || ''] || 'Utilisateur'}
                          </span>
                          {todo.assignedTo && (
                            <>
                              <span className="text-xs text-white/20">•</span>
                              <span className="text-xs text-white/30">
                                {agencyUsers.find(u => u.id === todo.assigned_to)?.firstName || 'Utilisateur'}
                              </span>
                            </>
                          )}
                        </div>
                        {todo.description && (
                          <p className="text-xs text-white/30 mt-2 line-clamp-2 break-words">
                            {todo.description}
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>

      <Dialog
        open={isModalOpen}
        onClose={handleCloseModal}
        className="fixed inset-0 z-10 overflow-y-auto"
      >
        <div className="flex items-center justify-center min-h-screen p-4">
          <Dialog.Overlay className="fixed inset-0 bg-black/60 backdrop-blur-sm" />
          <div className="relative bg-[#141414] border border-white/[0.08] rounded-2xl max-w-2xl w-full mx-4 p-6">
            <div className="absolute top-4 right-4">
              <button
                onClick={handleCloseModal}
                className="text-white/40 hover:text-white transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <h2 className="text-xl font-semibold text-white mb-4">
              {selectedTodo ? 'Modifier la tâche' : 'Nouvelle tâche'}
            </h2>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label htmlFor="title" className="block text-[10px] font-semibold text-white/40 uppercase tracking-widest mb-2">
                  Titre
                </label>
                {isInfluencerTask ? (
                  <div className="flex">
                    <div className="flex-shrink-0 bg-white/[0.08] text-white/60 px-3 py-2 rounded-l-lg border border-white/[0.08]">
                      {influencerPrefix}
                    </div>
                    <input
                      type="text"
                      value={customTitle}
                      onChange={(e) => {
                        setCustomTitle(e.target.value);
                        setFormData({ ...formData, title: `${influencerPrefix} – ${e.target.value}` });
                      }}
                      className="flex-1 bg-white/[0.06] border border-white/[0.08] border-l-0 rounded-r-lg px-3 py-2 text-white placeholder-white/30 focus:outline-none focus:ring-1 focus:ring-white/20"
                      placeholder="Description de la tâche"
                      required
                    />
                  </div>
                ) : (
                  <input
                    type="text"
                    id="title"
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    className="w-full bg-white/[0.06] border border-white/[0.08] rounded-xl px-3 py-2 text-white placeholder-white/30 focus:outline-none focus:ring-1 focus:ring-white/20"
                    placeholder="Titre de la tâche"
                    required
                  />
                )}
              </div>
              <div>
                <label htmlFor="description" className="block text-[10px] font-semibold text-white/40 uppercase tracking-widest mb-2">
                  Description
                </label>
                <textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="w-full bg-white/[0.06] border border-white/[0.08] rounded-xl px-3 py-2 text-white placeholder-white/30 focus:outline-none focus:ring-1 focus:ring-white/20"
                  placeholder="Description de la tâche"
                  rows={3}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label htmlFor="priority" className="block text-[10px] font-semibold text-white/40 uppercase tracking-widest mb-2">
                    Priorité
                  </label>
                  <CustomListbox
                    value={formData.priority}
                    onChange={(value) => setFormData({ ...formData, priority: value as Todo['priority'] })}
                    options={PRIORITIES.map(p => ({ value: p.value, label: p.label }))}
                  />
                </div>
                <div>
                  <label htmlFor="status" className="block text-[10px] font-semibold text-white/40 uppercase tracking-widest mb-2">
                    Statut
                  </label>
                  <CustomListbox
                    value={formData.status}
                    onChange={(value) => setFormData({ ...formData, status: value as Todo['status'] })}
                    options={STATUSES.map(s => ({ value: s.value, label: s.label }))}
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label htmlFor="dueDate" className="block text-[10px] font-semibold text-white/40 uppercase tracking-widest mb-2">
                    Date d'échéance
                  </label>
                  <input
                    type="date"
                    id="dueDate"
                    value={formData.dueDate ? new Date(formData.dueDate).toISOString().split('T')[0] : ''}
                    onChange={(e) => setFormData({ ...formData, dueDate: e.target.value })}
                    className="w-full bg-white/[0.06] border border-white/[0.08] rounded-xl px-3 py-2 text-white focus:outline-none focus:ring-1 focus:ring-white/20"
                    required
                  />
                </div>
                <div>
                  <label htmlFor="assignedTo" className="block text-[10px] font-semibold text-white/40 uppercase tracking-widest mb-2">
                    Assigné à
                  </label>
                  <CustomListbox
                    value={formData.assignedTo || ''}
                    onChange={(value) => setFormData({ ...formData, assignedTo: value })}
                    options={[
                      { value: '', label: 'Moi' },
                      ...agencyUsers.filter(user => user.id !== currentUser?.id).map(user => ({
                        value: user.id,
                        label: `${user.firstName} ${user.lastName}`
                      }))
                    ]}
                  />
                </div>
              </div>
              {error && (
                <div className="bg-red-500/10 border border-red-500/20 text-red-400 px-4 py-3 rounded-xl flex items-center gap-2">
                  <AlertCircle className="w-5 h-5 flex-shrink-0" />
                  <p>{error}</p>
                </div>
              )}
              <div className="flex justify-end gap-3">
                <button
                  type="button"
                  onClick={handleCloseModal}
                  className="px-4 py-2 text-white/60 bg-white/[0.06] border border-white/[0.08] rounded-xl hover:bg-white/[0.10] transition-colors"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-white text-black font-semibold rounded-xl hover:bg-white/90 transition-colors"
                >
                  {selectedTodo ? 'Mettre à jour' : 'Créer'}
                </button>
              </div>
            </form>
          </div>
        </div>
      </Dialog>
    </div>
  );
}

export default TodoPage;